import { Show } from "solid-js";
import {
  insuranceAnswer,
  type FormData,
  type AppointmentTemplate,
  type Provider,
  type Location,
} from "./types";
import { formatCurrency, useI18n, dateLocale } from "./i18n";
import { localeDate, localeTime } from "~/util/date/intl_dates";
import { Notice } from "./_notice";

type ReviewStepProps = {
  formData: FormData;
  /** The insurance step was offered; when it was skipped there is nothing to summarize. */
  insuranceInfoEnabled: boolean;
  selectedTemplate: AppointmentTemplate | null;
  selectedProvider: Provider | null;
  location: Location;
  locale: string;
  /** Set when this booking takes a deposit, which makes payment the next step. */
  depositAmountCents: number | null;
  onUpdate: (partial: Partial<FormData>) => void;
  onSubmit: () => void;
  onBack: () => void;
  submitting: boolean;
  errors: string[];
};

export function ReviewStep(props: ReviewStepProps) {
  const t = useI18n();
  const dl = () => dateLocale(props.locale);

  const insuranceIntake = () => insuranceAnswer(props.formData) === "yes";

  // Which sides were photographed, or null so the row is left out.
  const cardPhotos = () => {
    const sides = [
      props.formData.insuranceCardFront ? t("card_front_short") : null,
      props.formData.insuranceCardBack ? t("card_back_short") : null,
    ].filter((side) => side !== null);
    return sides.length > 0 ? sides.join(", ") : null;
  };

  const formatDateTime = () => {
    const slotKey = props.formData.appointmentSlotKey;
    if (!slotKey) return null;

    // slot key format: "opera_xxx::2026-02-19T10:00:00-05:00"
    const parts = slotKey.split("::");
    if (parts.length < 2) return null;

    const startTime = new Date(parts[1]!);
    const durationMs = (props.selectedTemplate?.durationMinutes ?? 60) * 60 * 1000;
    const endTime = new Date(startTime.getTime() + durationMs);

    const tz = props.location.timezone;
    const dateStr = localeDate(dl(), startTime, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    });
    const startStr = localeTime(dl(), startTime, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    const endStr = localeTime(dl(), endTime, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });

    return `${dateStr}, ${startStr} – ${endStr}`;
  };

  const patientName = () => {
    const nameParts = [props.formData.firstName, props.formData.lastName].filter(Boolean);
    if (props.formData.preferredName) {
      return `${nameParts.join(" ")} (${props.formData.preferredName})`;
    }
    return nameParts.join(" ");
  };

  const depositAmount = () => {
    const cents = props.depositAmountCents;
    if (!cents) return null;

    return formatCurrency(cents, props.locale);
  };

  const submitLabel = () => {
    if (props.submitting) return t("scheduling");

    return props.depositAmountCents ? t("continue_to_payment") : t("schedule_appointment_btn");
  };

  const formatPhone = () => {
    const digits = props.formData.phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return props.formData.phone;
  };

  return (
    <div class="flex flex-col gap-5">
      <h2 class="text-xl font-semibold text-typography-primary">{t("review_appointment")}</h2>

      <div class="flex flex-col divide-y divide-brand-subtle rounded-xl border border-brand-subtle">
        <div class="flex flex-col gap-3 p-4">
          <p class="text-xs font-semibold tracking-wider text-typography-secondary uppercase">
            {t("appointment_details")}
          </p>
          <ReviewRow label={t("type_label")} value={props.selectedTemplate?.name ?? "—"} />
          <ReviewRow
            label={t("provider")}
            value={
              props.selectedProvider?.name ??
              props.formData.appointmentSlotProviderName ??
              t("any_available")
            }
          />
          <ReviewRow label={t("date_and_time")} value={formatDateTime() ?? "—"} />
          <Show when={props.formData.chiefComplaint}>
            <ReviewRow label={t("notes")} value={props.formData.chiefComplaint} />
          </Show>
          <Show when={depositAmount()}>
            {(amount) => <ReviewRow label={t("deposit_due")} value={amount()} />}
          </Show>
        </div>

        <div class="flex flex-col gap-3 p-4">
          <p class="text-xs font-semibold tracking-wider text-typography-secondary uppercase">
            {t("patient_information")}
          </p>
          <ReviewRow label={t("name")} value={patientName()} />
          <Show when={props.formData.dob}>
            <ReviewRow
              label={t("date_of_birth")}
              value={localeDate(dl(), new Date(props.formData.dob + "T00:00:00"), {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            />
          </Show>
          <ReviewRow label={t("phone")} value={formatPhone()} />
          <Show when={props.formData.email}>
            <ReviewRow label={t("email")} value={props.formData.email} />
          </Show>
        </div>

        <Show when={props.insuranceInfoEnabled}>
          <div class="flex flex-col gap-3 p-4">
            <p class="text-xs font-semibold tracking-wider text-typography-secondary uppercase">
              {t("insurance")}
            </p>
            <Show
              when={insuranceIntake()}
              fallback={
                <p class="text-sm text-typography-secondary">
                  {props.formData.patientType === "existing_patient"
                    ? t("no_insurance_change")
                    : t("no_insurance")}
                </p>
              }
            >
              <Show when={props.formData.insuranceName}>
                <ReviewRow label={t("insurance")} value={props.formData.insuranceName} />
              </Show>
              <Show when={props.formData.insuranceSubscriberName}>
                <ReviewRow label={t("subscriber")} value={props.formData.insuranceSubscriberName} />
              </Show>
              <Show when={props.formData.insuranceSubscriberDob}>
                <ReviewRow
                  label={t("subscriber_dob")}
                  value={localeDate(
                    dl(),
                    new Date(props.formData.insuranceSubscriberDob + "T00:00:00"),
                    {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                />
              </Show>
              <Show when={props.formData.insuranceSubscriberId}>
                <ReviewRow
                  label={t("subscriber_id_label")}
                  value={props.formData.insuranceSubscriberId}
                />
              </Show>
              <Show when={props.formData.insuranceGroupNumber}>
                <ReviewRow label={t("group_hash")} value={props.formData.insuranceGroupNumber} />
              </Show>
              <Show when={cardPhotos()}>
                {(sides) => <ReviewRow label={t("card_photos")} value={sides()} />}
              </Show>
            </Show>
          </div>
        </Show>
      </div>

      <Show when={props.errors.length > 0}>
        <Notice messages={props.errors} />
      </Show>

      <label class="flex items-start gap-3 rounded-xl border border-brand-subtle p-3.5">
        <input
          type="checkbox"
          class="mt-0.5 size-4 rounded border-brand-subtle accent-brand-primary"
          checked={props.formData.waitlist}
          onChange={(e) => props.onUpdate({ waitlist: e.currentTarget.checked })}
        />
        <span class="text-sm text-typography-secondary">{t("waitlist")}</span>
      </label>

      <p class="text-xs text-typography-secondary">
        {t("consent_prefix")}
        <a href="/terms_of_use" target="_blank" class="underline hover:text-typography-primary">
          {t("terms")}
        </a>
        {t("consent_and")}
        <a href="/privacy_policy" target="_blank" class="underline hover:text-typography-primary">
          {t("privacy")}
        </a>
        {t("consent_suffix")}
      </p>

      <div class="flex items-center gap-3 pt-1">
        <button
          type="button"
          class="rounded-full border border-brand-subtle px-4 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98]"
          onClick={() => props.onBack()}
        >
          {t("back")}
        </button>
        <button
          type="button"
          class="ml-auto rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all brand-button-bg hover:brand-button-bg-active active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          disabled={props.submitting}
          onClick={() => props.onSubmit()}
        >
          {submitLabel()}
        </button>
      </div>
    </div>
  );
}

function ReviewRow(props: { label: string; value: string }) {
  return (
    <div class="flex items-baseline justify-between gap-4">
      <span class="shrink-0 text-sm text-typography-secondary">{props.label}</span>
      <span class="text-right text-sm font-medium text-typography-primary">{props.value}</span>
    </div>
  );
}
